import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { AuthShell, Field, primary, link, errText } from '../components/AuthShell';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post('/auth/forgot-password', { email }); setSent(true); } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="Enter your work email and we'll send a one-time link (valid 1 hour)." testId="forgot-password-page"
      footer={<a href="/login" style={link} data-testid="forgot-back-link">Back to sign in</a>}>
      {sent ? <p data-testid="forgot-sent" style={{ color: '#86EFAC' }}>If that email belongs to an account, a reset link is on its way. Check your inbox (and spam folder).</p> : (
        <form onSubmit={submit}>
          <Field id="forgot-email" type="email" value={email} onChange={setEmail} placeholder="you@yourshop.com" required>Email</Field>
          <button type="submit" style={primary} disabled={busy} data-testid="forgot-submit">{busy ? 'Sending…' : 'Send reset link'}</button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const token = params.get('token') || '';
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error('Passwords do not match');
    setBusy(true);
    try { await axios.post('/auth/reset-password', { token, password: pw }); toast.success('Password updated — please sign in'); nav('/login'); }
    catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Choose a new password" testId="reset-password-page" footer={<a href="/forgot-password" style={link}>Request a new link</a>}>
      {!token ? <p style={{ color: '#FCA5A5' }} data-testid="reset-missing-token">This link is missing its token. Please use the link from your email.</p> : (
        <form onSubmit={submit}>
          <Field id="reset-password" type="password" value={pw} onChange={setPw} required minLength={8} autoComplete="new-password">New password</Field>
          <Field id="reset-password-confirm" type="password" value={pw2} onChange={setPw2} required minLength={8} autoComplete="new-password">Confirm password</Field>
          <button type="submit" style={primary} disabled={busy} data-testid="reset-submit">{busy ? 'Saving…' : 'Set new password'}</button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState('working');
  useEffect(() => {
    const token = params.get('token');
    if (!token) return setState('missing');
    axios.post('/auth/verify-email', { token }).then(() => setState('ok')).catch(() => setState('bad'));
  }, [params]);
  const msg = { working: 'Verifying…', ok: 'Your email is verified. You can close this tab or continue to your dashboard.', bad: 'This verification link is invalid or has expired. Sign in and click "Resend" in the banner to get a new one.', missing: 'This link is missing its token.' }[state];
  return (
    <AuthShell title="Email verification" testId="verify-email-page" footer={<a href="/" style={link} data-testid="verify-continue-link">Continue to dashboard</a>}>
      <p data-testid={`verify-state-${state}`} style={{ color: state === 'ok' ? '#86EFAC' : state === 'working' ? '#CBD5E1' : '#FCA5A5' }}>{msg}</p>
    </AuthShell>
  );
}

export function AcceptInvite() {
  const { login } = useContext(AuthContext);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!token) return setErr('This link is missing its token.');
    axios.get('/auth/invite-info', { params: { token } }).then((r) => { setInfo(r.data); setName(r.data.name || ''); }).catch((e) => setErr(errText(e, 'This invitation is invalid or has expired')));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await axios.post('/auth/accept-invite', { token, password: pw, name }); login(data.token, data.user); toast.success(`Welcome to ${data.user.company?.name}!`); nav('/'); }
    catch (e2) { toast.error(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title={info ? `Join ${info.company_name}` : 'Team invitation'} subtitle={info ? `You're invited as ${info.role} · ${info.email}` : undefined} testId="accept-invite-page">
      {err ? <p data-testid="invite-error" style={{ color: '#FCA5A5' }}>{err}</p> : info && (
        <form onSubmit={submit}>
          <Field id="invite-name" value={name} onChange={setName} placeholder="Your full name" required>Your name</Field>
          <Field id="invite-password" type="password" value={pw} onChange={setPw} required minLength={8} autoComplete="new-password">Choose a password</Field>
          <button type="submit" style={primary} disabled={busy} data-testid="invite-submit">{busy ? 'Joining…' : 'Accept invitation'}</button>
        </form>
      )}
    </AuthShell>
  );
}
